import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Phone, Star, Sparkles, Clock, Users, Award, HardHat, HeartPulse, Sprout, Landmark, MessageCircle, MapPin } from "lucide-react";
import SEO from "@/components/SEO";
import ContactForm from "@/components/ContactForm";
import { SERVICES, COMPANY } from "@/data/services";
import { SITE_URL } from "@/config/site";

const ICONS = { HardHat, HeartPulse, ShieldCheck, Sprout, Landmark };

const testimonials = [
  {
    name: "Nadine Chaffal",
    role: "Nadine Agencement & Design — Local Guide (108 avis)",
    text: "Dans le cadre du développement de mon activité d'architecte d'intérieur, j'ai vraiment eu beaucoup de mal à trouver un assureur compétent, compétitif, et à l'écoute de mes besoins en terme de garanties. Julien Maertens a été parfait sur ces 3 points. Je suis sereine sur l'avenir de mon agence désormais. Je recommande ++.",
    rating: 5,
    date: "il y a 2 mois"
  },
  {
    name: "Jérôme Gomez",
    role: "Chef d'entreprise — Décennale RC Pro",
    text: "J'ai eu besoin d'une décennale rc pro à l'ouverture de ma société et Julien Maertens a été non seulement à l'écoute mais réactif et a su réagir à chaque fois en temps et en heure pour me trouver l'offre la plus adaptée. Je recommande.",
    rating: 5,
    date: "il y a 2 mois"
  },
  {
    name: "Sophie",
    role: "Cliente — 4 avis, 4 photos",
    text: "Ravie d'avoir pu contractualiser avec ce courtier et grâce à mon interlocuteur et conseiller Julien Maertens qui a été très professionnel et très efficace dans la démarche du montage de mon dossier aussi bien en terme de conditions qu'en terme de délais. Je recommande vivement.",
    rating: 5,
    date: "il y a une semaine"
  },
  {
    name: "Pascaline Moulin",
    role: "Cliente — Décennale",
    text: "Nous avons été satisfait de faire appel à Perspective Assurance, en particulier M. Maertens qui a su écouter nos besoins et nous proposer une décennale à la hauteur de nos attentes. Nous le recommandons. Disponible et réactif.",
    rating: 5,
    date: "il y a 2 mois"
  },
  {
    name: "Mohamed ali Moumen",
    role: "Client — Décennale",
    text: "Service au top je vous le recommande vivement ++++",
    rating: 5,
    date: "il y a un mois"
  },
  {
    name: "Florent Hoareau",
    role: "Client — Décennale",
    text: "Super réactif et sérieux 👍",
    rating: 5,
    date: "il y a un mois"
  }
];

export default function Home() {
  return (
    <>
      <SEO
        title="Perspective Assurance — Courtier à Lyon | Décennale, Mutuelle, Prévoyance, Vie, PER"
        description="Courtier en assurances à Lyon. Spécialiste en décennale BTP, mutuelle santé, prévoyance, assurance vie et PER. Devis gratuit, accompagnement personnalisé."
        canonical={`${SITE_URL}/`}
      />

      {/* HERO */}
      <section className="relative overflow-hidden bg-cream" data-testid="hero-section">
        <div className="absolute inset-0 grain pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 pt-12 lg:pt-16 pb-16 lg:pb-24 relative">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            <div className="lg:col-span-6 animate-fade-in-up">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-soft rounded-full mb-6">
                <Sparkles className="w-3.5 h-3.5 text-terracotta" />
                <span className="font-outfit text-xs tracking-[0.2em] uppercase text-forest/80">Courtier indépendant à Lyon</span>
              </div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-forest leading-[1.05] tracking-tight text-balance">
                L'assurance,<br />
                <span className="italic text-terracotta">vraiment</span> à votre mesure.
              </h1>
              <p className="mt-6 text-base sm:text-lg text-forest/70 leading-relaxed max-w-xl">
                Décennale, mutuelle, prévoyance, assurance vie, plan d'épargne retraite : nous comparons les meilleures compagnies pour vous proposer la garantie juste — au juste prix.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <a href="#devis-hero" data-testid="hero-cta-quote" className="inline-flex items-center justify-center gap-2 bg-terracotta hover:bg-[#1E3F73] text-white rounded-full px-8 py-4 font-outfit font-medium shadow-md hover:shadow-lg transition-all">
                  Obtenir mon devis gratuit
                  <ArrowRight className="w-4 h-4" />
                </a>
                <a
                  href={`tel:${COMPANY.phone}`}
                  data-testid="hero-cta-call"
                  className="inline-flex items-center justify-center gap-2 bg-white border border-soft text-forest hover:border-forest rounded-full px-8 py-4 font-outfit font-medium transition-all"
                >
                  <Phone className="w-4 h-4" /> {COMPANY.phoneDisplay}
                </a>
              </div>

              <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4 text-sm text-forest/70">
                <div className="flex items-center gap-2"><Star className="w-4 h-4 text-terracotta" fill="#2C5EAD" /> +50 avis 5★ Google</div>
                <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-terracotta" /> Réponse sous 24h</div>
                <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-terracotta" /> Sans engagement</div>
              </div>
            </div>

            <div id="devis-hero" className="lg:col-span-6 animate-fade-in-up animate-delay-200 scroll-mt-24">
              <div className="relative">
                <div className="absolute -inset-3 bg-terracotta/10 rounded-3xl rotate-1 hidden lg:block" />
                <div className="relative bg-white border border-soft rounded-3xl shadow-2xl p-6 sm:p-8 lg:p-10" data-testid="hero-form-card">
                  <div className="mb-6">
                    <div className="font-outfit text-[11px] tracking-[0.25em] uppercase text-terracotta mb-2">Devis gratuit — Sans engagement</div>
                    <h2 className="font-display text-2xl sm:text-3xl text-forest leading-tight">Recevez votre proposition en 24h.</h2>
                    <p className="mt-2 text-sm text-forest/65">Remplissez ce formulaire, un conseiller vous rappelle rapidement.</p>
                  </div>
                  <ContactForm source="hero-home" compact={false} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-20 lg:py-32 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
          <div className="max-w-2xl mb-16">
            <div className="font-outfit text-xs tracking-[0.3em] uppercase text-terracotta mb-4">Nos prestations</div>
            <h2 className="font-display text-4xl sm:text-5xl text-forest tracking-tight leading-tight">
              Cinq expertises,<br />une seule promesse&nbsp;: <span className="italic">vous protéger.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s, i) => {
              const Icon = ICONS[s.icon] || ShieldCheck;
              return (
                <Link
                  key={s.slug}
                  to={`/${s.slug}`}
                  data-testid={`service-card-${s.slug}`}
                  className={`group bg-cream p-8 rounded-2xl border border-soft hover:border-terracotta hover:-translate-y-1 hover:shadow-xl transition-all duration-300 ${i === 0 ? "md:col-span-2 lg:col-span-1" : ""}`}
                >
                  <div className="w-12 h-12 rounded-xl bg-forest text-white flex items-center justify-center mb-6 group-hover:bg-terracotta transition-colors">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="font-display text-2xl text-forest mb-3">{s.title}</h3>
                  <p className="text-forest/70 text-sm leading-relaxed mb-6">{s.short}</p>
                  <div className="flex items-center gap-2 text-terracotta font-outfit text-sm font-medium">
                    En savoir plus <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="py-20 lg:py-32 bg-cream" data-testid="why-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="font-outfit text-xs tracking-[0.3em] uppercase text-terracotta mb-4">Pourquoi nous</div>
            <h2 className="font-display text-4xl sm:text-5xl text-forest leading-tight mb-8">
              Un courtier, <span className="italic">pas un robot.</span>
            </h2>
            <div className="space-y-6">
              {[
                { icon: Users, title: "Un interlocuteur unique", text: "Julien vous accompagne personnellement, de la première question jusqu'à la gestion d'un éventuel sinistre." },
                { icon: Award, title: "Indépendance totale", text: "Nous comparons sans biais une vingtaine de compagnies pour vous proposer la meilleure offre du marché." },
                { icon: Clock, title: "Réactivité maximale", text: "Devis sous 24h, attestation décennale en 48h, suivi pendant toute la durée de votre contrat." },
                { icon: MapPin, title: "Proximité lyonnaise", text: "Bureau à Lyon 6e, intervention en métropole et DOM-TOM. On se déplace si besoin." }
              ].map((item, i) => (
                <div key={i} className="flex gap-4">
                  <div className="w-10 h-10 rounded-full bg-white border border-soft flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 text-terracotta" />
                  </div>
                  <div>
                    <h3 className="font-outfit font-semibold text-forest mb-1">{item.title}</h3>
                    <p className="text-forest/70 text-sm leading-relaxed">{item.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <div className="absolute -inset-4 bg-forest/5 rounded-3xl -rotate-2" />
            <img
              src="https://images.pexels.com/photos/34811176/pexels-photo-34811176.jpeg"
              alt="Lyon, siège de Perspective Assurance"
              className="relative rounded-3xl shadow-xl w-full aspect-[4/5] object-cover"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 lg:py-32 bg-white" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div className="max-w-2xl">
              <div className="font-outfit text-xs tracking-[0.3em] uppercase text-terracotta mb-4">Avis vérifiés Google</div>
              <h2 className="font-display text-4xl sm:text-5xl text-forest leading-tight">Ils nous ont fait confiance.</h2>
            </div>
            <div className="flex items-center gap-4 bg-cream border border-soft rounded-2xl px-5 py-4">
              <div className="flex flex-col items-center">
                <span className="font-display text-3xl text-forest leading-none">5,0</span>
                <div className="flex gap-0.5 mt-1">
                  {[...Array(5)].map((_, j) => <Star key={j} className="w-3 h-3 text-terracotta" fill="#2C5EAD" />)}
                </div>
              </div>
              <div>
                <div className="font-outfit font-semibold text-forest text-sm">Note Google</div>
                <a href="https://www.google.com/search?q=Perspective+Assurance+Lyon+avis" target="_blank" rel="noopener noreferrer" className="text-xs text-forest/60 hover:text-terracotta underline underline-offset-2">Sur +50 avis clients</a>
              </div>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div key={i} data-testid={`testimonial-${i}`} className="bg-cream p-7 rounded-2xl border border-soft flex flex-col">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex gap-0.5">
                    {[...Array(t.rating)].map((_, j) => <Star key={j} className="w-4 h-4 text-terracotta" fill="#2C5EAD" />)}
                  </div>
                  <span className="text-xs text-forest/50">{t.date}</span>
                </div>
                <p className="text-forest/80 leading-relaxed mb-5 text-[15px] flex-1">"{t.text}"</p>
                <div className="pt-4 border-t border-soft flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-forest text-white flex items-center justify-center font-outfit font-semibold text-sm flex-shrink-0">
                    {t.name.split(' ').map(n => n[0]).slice(0, 2).join('')}
                  </div>
                  <div className="min-w-0">
                    <div className="font-outfit font-semibold text-forest text-sm truncate">{t.name}</div>
                    <div className="text-xs text-forest/60 truncate">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-12 text-center">
            <a
              href="https://www.google.com/search?q=Perspective+Assurance+Lyon+avis"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="google-reviews-link"
              className="inline-flex items-center gap-2 bg-white border border-soft hover:border-forest text-forest rounded-full px-6 py-3 font-outfit font-medium text-sm transition-all"
            >
              Voir tous les avis Google →
            </a>
          </div>
        </div>
      </section>

      {/* CTA FORM */}
      <section className="py-20 lg:py-32 bg-forest text-white relative overflow-hidden" data-testid="cta-form-section">
        <div className="absolute inset-0 grain pointer-events-none opacity-30" />
        <div className="max-w-6xl mx-auto px-6 md:px-10 lg:px-12 grid lg:grid-cols-2 gap-12 lg:gap-20 relative">
          <div>
            <div className="font-outfit text-xs tracking-[0.3em] uppercase text-terracotta mb-4">Demande de devis</div>
            <h2 className="font-display text-4xl sm:text-5xl text-white leading-tight mb-6">
              Parlons de votre situation.
            </h2>
            <p className="text-white/70 text-lg leading-relaxed mb-8">
              Quelques informations suffisent. Nous étudions votre profil et revenons vers vous avec une proposition claire, sans jargon.
            </p>
            <div className="space-y-4">
              <a href={`tel:${COMPANY.phone}`} className="flex items-center gap-4 group" data-testid="cta-phone">
                <div className="w-12 h-12 rounded-full bg-terracotta/20 group-hover:bg-terracotta flex items-center justify-center transition-colors">
                  <Phone className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-white/50">Téléphone</div>
                  <div className="font-outfit text-lg">{COMPANY.phoneDisplay}</div>
                </div>
              </a>
              <a href={COMPANY.whatsapp} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 group" data-testid="cta-whatsapp">
                <div className="w-12 h-12 rounded-full bg-terracotta/20 group-hover:bg-terracotta flex items-center justify-center transition-colors">
                  <MessageCircle className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-white/50">WhatsApp</div>
                  <div className="font-outfit text-lg">Réponse en quelques minutes</div>
                </div>
              </a>
            </div>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-3xl shadow-2xl">
            <ContactForm source="homepage" />
          </div>
        </div>
      </section>
    </>
  );
}
